/**
 * ProfilePage — Broker user profile surface.
 *
 * Displays user information (name, email, roles) from Keycloak auth context.
 * Read-only view — user management happens in Keycloak admin console.
 *
 * Refs: DOPLA-599 — As a broker user I need a profile page
 */

import { useAuth } from '../auth/KeycloakProvider';
import { t } from '../i18n';

export default function ProfilePage() {
  const { user, authenticated } = useAuth();

  if (!authenticated || !user) {
    return (
      <div className="dp-shell__content" style={{ padding: 'var(--sp-8)' }}>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)' }}>
          {t('profile.notAuthenticated')}
        </p>
      </div>
    );
  }

  return (
    <div className="dp-shell__content" style={{ padding: 'var(--sp-8)' }}>
      <div style={{ maxWidth: '600px' }}>
        <p className="dp-eyebrow">{t('profile.eyebrow')}</p>
        <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-6)' }}>{t('profile.title')}</h2>

        {/* Personal Information */}
        <section
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--sp-6)',
            marginBottom: 'var(--sp-6)',
          }}
        >
          <h3
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              fontWeight: 600,
              color: 'var(--fg-1)',
              margin: '0 0 var(--sp-4)',
            }}
          >
            {t('profile.personalInfo')}
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 500,
                  color: 'var(--fg-2)',
                  marginBottom: 'var(--sp-1)',
                }}
              >
                {t('profile.firstName')}
              </label>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body)',
                  color: 'var(--fg-1)',
                  margin: 0,
                }}
              >
                {user.firstName}
              </p>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 500,
                  color: 'var(--fg-2)',
                  marginBottom: 'var(--sp-1)',
                }}
              >
                {t('profile.lastName')}
              </label>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body)',
                  color: 'var(--fg-1)',
                  margin: 0,
                }}
              >
                {user.lastName}
              </p>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 500,
                  color: 'var(--fg-2)',
                  marginBottom: 'var(--sp-1)',
                }}
              >
                {t('profile.email')}
              </label>
              <p
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-body)',
                  color: 'var(--fg-1)',
                  margin: 0,
                }}
              >
                {user.email}
              </p>
            </div>
          </div>
        </section>

        {/* Roles & Permissions */}
        {user.roles && user.roles.length > 0 && (
          <section
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-6)',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body)',
                fontWeight: 600,
                color: 'var(--fg-1)',
                margin: '0 0 var(--sp-4)',
              }}
            >
              {t('profile.rolesPermissions')}
            </h3>

            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--sp-2)',
              }}
            >
              {user.roles.map((role) => (
                <li
                  key={role}
                  style={{
                    display: 'inline-block',
                    padding: 'var(--sp-1) var(--sp-3)',
                    background: 'var(--dp-navy-100)',
                    color: 'var(--dp-navy-800)',
                    borderRadius: 'var(--radius-pill)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-meta)',
                    fontWeight: 500,
                  }}
                >
                  {role}
                </li>
              ))}
            </ul>

            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body-sm)',
                color: 'var(--fg-3)',
                marginTop: 'var(--sp-4)',
                marginBottom: 0,
              }}
            >
              {t('profile.rolesNote')}
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
